import React from 'react';
import { Stack, Tooltip, Text, themeVars, Button } from '@eds-web/react';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { LOGOS } from '../../constants/logo';
import { PrizeHeaderContent, FNLogoContent } from './Prizes.style';
import { device } from '../../constants/breakpoint';

interface HeaderData {
    label: string;
    value: string;
}

interface PrizeHeaderProps {
    headerData: HeaderData[];
    gameId: string;
    prizeId?: string;
    showClaimButton?: boolean;
}

const GAME_TITLE = {
    FORTNITE: 'fortnite',
    ROCKET_LEAGUE: 'rocketleague',
    SIDE_SWIPE: 'sideswipe',
    FALL_GUYS: 'fallguys'
};

const MobilePrizeInfo = styled.div`
    display: none;

    @media screen and ${device.laptop} {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 24px 0;
    }
    & div {
        width: 100%;
    }
`;

const MobileInfoRow = styled.div`
    gap: 4px;
    display: flex;
    flex-direction: column;
    flex: 0 0 50%;
    margin-bottom: 24px;
`;

const MobileContent = styled.div`
    display: flex;
    flex-wrap: wrap !important;
`;

const MobileInfoLabel = styled(Text)`
    color: ${themeVars.color.transparent.light['100']};
    min-width: 60px;
`;

const MobileInfoValue = styled(Text)`
    color: ${themeVars.color.transparent.light['065']};
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const HeaderContent = styled(Stack)`
    flex-wrap: inherit;
    @media screen and ${device.laptop} {
        display: none;
    }
`;

const renderGameLogo = (gameId: string) => {
    let logo = LOGOS.FN;
    switch (gameId) {
        case GAME_TITLE.FORTNITE:
            logo = LOGOS.FN;
            break;
        case GAME_TITLE.FALL_GUYS:
            logo = LOGOS.FG;
            break;
        case GAME_TITLE.ROCKET_LEAGUE:
            logo = LOGOS.RL;
            break;
        case GAME_TITLE.SIDE_SWIPE:
            logo = LOGOS.SS;
            break;
        default:
            break;
    }
    return logo;
};

export const PrizeSummary: React.FC<PrizeHeaderProps> = ({
    headerData,
    gameId,
    prizeId,
    showClaimButton
}) => {
    const history = useHistory();
    const gameLogo = renderGameLogo(gameId);
    const { t } = useTranslation();

    const handleHeaderClick = () => {
        if (prizeId) {
            history.push(`${window.appContext}/v2/prize-detail?prizeId=${prizeId}`);
        }
    };

    return (
        <>
            <HeaderContent
                direction="row"
                style={{ alignItems: 'center', height: 50, padding: '24px 0' }}
            >
                <FNLogoContent>
                    <svg viewBox={gameLogo.viewBox}>{gameLogo.data}</svg>
                </FNLogoContent>
                {headerData.map((item, index) => (
                    <PrizeHeaderContent
                        key={index}
                        variant="paragraphMD"
                        $isEvent={item.label === t('epic.esport.prizes.list.header.event')}
                    >
                        <Tooltip align={'start'} label={<span>{item.value}</span>}>
                            <p>{item.value}</p>
                        </Tooltip>
                    </PrizeHeaderContent>
                ))}
                {showClaimButton ? (
                    <Button style={{ width: 70 }} variant={'solid'} onClick={handleHeaderClick}>
                        {t('epic.esport.actions.claim')}
                    </Button>
                ) : (
                    <div style={{ width: 72 }} />
                )}
            </HeaderContent>
            <MobilePrizeInfo>
                <FNLogoContent>
                    <svg viewBox={gameLogo.viewBox}>{gameLogo.data}</svg>
                </FNLogoContent>
                <MobileContent>
                    {headerData.map((item, index) => (
                        <MobileInfoRow key={index}>
                            <MobileInfoLabel variant="uiMD">{item.label}</MobileInfoLabel>
                            <MobileInfoValue variant="uiMD">{item.value}</MobileInfoValue>
                        </MobileInfoRow>
                    ))}
                </MobileContent>
                {showClaimButton ? (
                    <Button variant={'solid'} size={'xl'} onClick={handleHeaderClick}>
                        {t('epic.esport.actions.claim')}
                    </Button>
                ) : (
                    <div style={{ width: 62 }} />
                )}
            </MobilePrizeInfo>
        </>
    );
};
